import { Flex, ScrollAreaAutosize } from "@mantine/core";
import { BoxTitle, Container } from "./StyledComponents";
import styled, { keyframes } from "styled-components";
import { theme } from "../../theme";
import { BuildingOffice2Icon } from "@heroicons/react/24/outline";
import { useFilterContext } from "../../context/DashboardFilterContext";
import { getLiveOccupancy } from "../../api/dashboardApi";
import { useCallback, useEffect, useState } from "react";
import TimeCounter from "../common/TimeCounter";

/**
 * ŞU AN BİNADA (plan 2.32).
 *
 * Backend'deki `/dashboard/liveOccupancy` ucu vardı ama HİÇ İSTEMCİSİ YOKTU;
 * bu yüzden projeksiyonu eskimiş personel modeline göre kalmış ve departman/rol
 * boş dönüyormuş. Uç düzeltildi, bu kutu da onu görünür kılıyor.
 *
 * "İçeride" ölçütü backend'de `insideNowStages`: son 24 SAAT içindeki son
 * hareketi giriş olanlar. Gün sınırı değil 24 saat kullanılmasının sebebi
 * gece vardiyası (plan 2.27) — ayrıntı serviste yazılı.
 */

type OccupancyRow = {
  employeeId: string;
  employeeName: string;
  employeeDepartment: string;
  employeeRole: string;
  since: string;
};

function initials(name: string) {
  return (name ?? "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** Girişten bu yana geçen süre, `TimeCounter`'ın beklediği "HH:MM:SS" biçiminde. */
function elapsedSince(since: string): string {
  const start = new Date(since).getTime();
  if (Number.isNaN(start)) return "00:00:00";
  const total = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const h = String(Math.floor(total / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

const InsideNow = ({ flex }: { flex: any }) => {
  const { filters } = useFilterContext();
  const [employees, setEmployees] = useState<OccupancyRow[]>([]);

  const load = useCallback(async () => {
    try {
      const response: any = await getLiveOccupancy(filters);
      if (response?.success) setEmployees(response?.data?.employees || []);
      else setEmployees([]);
    } catch (error) {
      setEmployees([]);
    }
  }, [filters]);

  useEffect(() => {
    load();
    // Canlı kutu: dakikada bir tazelenir. Sayaç zaten saniye saniye ilerliyor,
    // bu yenileme yalnızca GİRİP ÇIKANLARI yakalamak için.
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  return (
    <Container $flex={flex}>
      <HeaderRow>
        <Flex align="center" gap={10}>
          <HeaderIcon>
            <BuildingOffice2Icon width={18} />
          </HeaderIcon>
          <BoxTitle>Şu An Binada</BoxTitle>
        </Flex>
        <CountBadge>{employees.length}</CountBadge>
      </HeaderRow>

      <ScrollAreaAutosize h="90%" scrollbarSize={4}>
        {employees.length === 0 ? (
          /*
           * Boş durum BİLEREK var: gece ya da mesai dışında kimsenin binada
           * olmaması NORMAL. Boş bir kutu "veri gelmedi" gibi okunur ve
           * 2.32'nin gizli kalmasının sebebi tam olarak buydu.
           */
          <EmptyState>Şu anda binada kimse görünmüyor.</EmptyState>
        ) : (
          <EmpList>
            {employees.map((employee, index) => (
              <div key={employee.employeeId}>
                <EmpRow $delay={index * 45}>
                  <GradAvatar>{initials(employee.employeeName)}</GradAvatar>

                  <EmpInfo>
                    <EmpName>{employee.employeeName}</EmpName>
                    <EmpMetaColumn>
                      <span className="dept">{employee.employeeDepartment}</span>
                      <span className="role">{employee.employeeRole}</span>
                    </EmpMetaColumn>
                  </EmpInfo>

                  <InsideBadge>
                    <Dot />
                    <InsideTime>
                      {/*
                        `key` şart: TimeCounter başlangıç değerini yalnızca ilk
                        render'da okuyor. Liste tazelenip kişinin girişi
                        değiştiğinde bileşen yeniden kurulmazsa sayaç eski
                        değerden saymaya devam ederdi.
                      */}
                      <TimeCounter
                        key={`${employee.employeeId}-${employee.since}`}
                        initialTime={elapsedSince(employee.since)}
                      />
                    </InsideTime>
                  </InsideBadge>
                </EmpRow>
                {index !== employees.length - 1 && <ListDivider />}
              </div>
            ))}
          </EmpList>
        )}
      </ScrollAreaAutosize>
    </Container>
  );
};

export { InsideNow };
export default InsideNow;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.35; }
`;

const AVATAR_COLORS = {
  from: "#c9e5d4",
  to: "#93cfae",
};

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${theme.colors.brand.normal}15;
  color: ${theme.colors.brand.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid ${theme.colors.brand.normal}20;
`;

const CountBadge = styled.div`
  min-width: 36px;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
  font-size: 13.5px;
  color: #94a3b8;
  font-weight: 500;
  text-align: center;
  padding: 0 24px;
`;

const EmpList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 4px;
`;

const EmpRow = styled.div<{ $delay: number }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 12px;
  margin: 0 2px;
  animation: ${slideUp} 0.3s ease both;
  animation-delay: ${(p) => p.$delay}ms;
  border-radius: 12px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  background: transparent;

  &:hover {
    transform: translateY(-2px) scale(1.015);
    background: #ffffff;
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.08),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 10;
  }

  &:active {
    transform: translateY(-1px) scale(0.98);
  }
`;

const GradAvatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 30px;
  background: linear-gradient(135deg, ${AVATAR_COLORS.from}, ${AVATAR_COLORS.to});
  color: white;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const EmpInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const EmpName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmpMetaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 2px;

  .dept {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .role {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const InsideBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 50px;
  flex-shrink: 0;
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #16a34a;
  animation: ${pulse} 2s ease-in-out infinite;
  flex-shrink: 0;
`;

const InsideTime = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: #15803d;
  font-variant-numeric: tabular-nums;
  min-width: 62px;
`;

const ListDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin: 0 10px;
`;
